/* eslint-disable max-classes-per-file */
import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { map } from 'rxjs';
import { UserDTO } from 'src/app/common/state/user/user.dto';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { select } from 'src/app/common/utilities/ngxs-utils';
import { CoacheeListService } from 'src/app/private/shared/services/coachee-list/coachee-list.service';
import { CoacheeDTO } from 'src/app/private/shared/types/responses/coaching-log.responses';

import {
  SortableHeader,
  SortEvent,
} from '../../shared/directives/sortable-header.directive';
import { NewCoachingSessionUserDto } from '../../shared/dtos/coaching-session.dto';
import { CoachingLogService } from '../../shared/services/coaching-log/coaching-log.service';
import { FilterTableSearch } from './search-filter.pipe';
import { SelectableCoachee } from './selectable.coachee';

interface CoacheeListSortIcon {
  name: 'gray' | 'none';
  badges: 'gray' | 'none';
  celebrations: 'gray' | 'none';
  sessions: 'gray' | 'none';
  classroomVisits: 'gray' | 'none';
  nextSession: 'gray' | 'none';
}

const compare = (v1: string | number, v2: string | number) =>
  /* eslint-disable-next-line no-nested-ternary */
  v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

const columnDataMap = (
  coachee: CoacheeDTO,
  column: string
): string | number => {
  switch (column) {
    case 'badges':
      return coachee.badgeCount || 0;
    case 'celebrations':
      return coachee.celebrationsCount || 0;
    case 'sessions':
      return coachee.sessionCount || 0;
    case 'classroomVisits':
      return coachee.classroomvisitCount || 0;
    case 'nextSession':
      return coachee.nextSessionDate || '';
    case 'name':
    default:
      return `${coachee.profile.last_name} ${coachee.profile.first_name}`;
  }
};

@Component({
  selector: 'app-coachee-list-page',
  templateUrl: './coachee-list-page.component.html',
  styleUrls: ['./coachee-list-page.component.scss'],
  providers: [FilterTableSearch],
})
export class CoacheeListPageComponent implements OnInit {
  @ViewChildren(SortableHeader) headers: QueryList<SortableHeader>;

  onSort({ column, direction }: SortEvent) {
    // resetting other headers
    this.headers.forEach((header) => {
      if (header.sortable !== column) {
        header.direction = '';
        this.sortIconTemp[<keyof CoacheeListSortIcon>header.sortable] = 'gray';
      } else {
        this.sortIconTemp[<keyof CoacheeListSortIcon>header.sortable] = 'none';
      }
    });

    if (direction === '' || column === '') {
      // this.tableData = this.coachees;
      this.sortIconTemp[<keyof CoacheeListSortIcon>column] = 'gray';
    } else {
      this.tableData = [...this.tableData].sort((a, b) => {
        const res = compare(
          columnDataMap(a, column as string),
          columnDataMap(b, column as string)
        );
        return direction === 'asc' ? res : -res;
      });
    }
  }

  user$ = select(UserState.getUser);

  user: User | null = null;

  dayOfWeek = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  coachees: SelectableCoachee[];

  tableData: SelectableCoachee[];

  selectedCoacheesIds: number[] = [];

  attendeeUsers: UserDTO[] | null;

  attendeeUserProfiles: NewCoachingSessionUserDto[] | null;

  masterSelected = false;

  totalCoachees: number;

  calendarIconStatus = 'disabled';

  isLoading = false;

  sortIconTemp: CoacheeListSortIcon = {
    name: 'gray',
    badges: 'gray',
    celebrations: 'gray',
    sessions: 'gray',
    classroomVisits: 'gray',
    nextSession: 'gray',
  };

  constructor(
    private router: Router,
    private coacheeListService: CoacheeListService,
    private coachingLogService: CoachingLogService
  ) {}

  async ngOnInit(): Promise<void> {
    this.user$
      .pipe(
        map((user) => {
          this.user = user;
        })
      )
      .subscribe();

    if (!this.attendeeUserProfiles) {
      this.attendeeUserProfiles = [];
    }

    this.getCoachees();
  }

  getCoachees(): void {
    this.isLoading = true;
    this.coacheeListService
      .fetchCoachees({ details: 1, per_page: 500 })
      .subscribe(
        (coachees) => {
          if (coachees) {
            this.coachees = coachees.items as SelectableCoachee[];
            this.tableData = this.coachees;
            this.totalCoachees = coachees?._meta.totalCount;
            this.getCheckedItemList();
            this.isLoading = false;
          }
        },
        () => {
          this.isLoading = false;
        }
      );
  }

  searchCoachees(event: Event) {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.tableData = new FilterTableSearch().transform(
      this.coachees,
      searchTerm
    );
    // resetting other headers
    this.headers.forEach((header) => {
      header.direction = '';
      this.sortIconTemp[<keyof CoacheeListSortIcon>header.sortable] = 'gray';
    });
  }

  createNewLog() {
    if (this.selectedCoacheesIds.length > 0) {
      if (this.user?.id && this.coachees) {
        this.coachingLogService
          .createLog(this.user.id, this.selectedCoacheesIds, true)
          .subscribe();
      }
    }
  }

  onCheckboxChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (target) {
      if (target.checked) {
        this.selectedCoacheesIds.push(parseInt(target.value));
      } else {
        const index = this.selectedCoacheesIds.findIndex(
          (value) => value === parseInt(target.value)
        );
        this.selectedCoacheesIds.splice(index, 1);
      }
      this.checkIfAllSelected();
      this.handleCalendarIconStatus();
      this.updateCoachlogAttendees();
    }
  }

  getCheckedItemList(): void {
    this.coachees.forEach((item: SelectableCoachee) => {
      item.isSelected = this.masterSelected;
    });
  }

  checkIfAllSelected() {
    this.masterSelected = this.coachees.every(
      (item: SelectableCoachee) => item.isSelected === true
    );
  }

  handleCheckAll(): void {
    this.selectedCoacheesIds = [];
    this.coachees.forEach((item: SelectableCoachee) => {
      item.isSelected = this.masterSelected;
      if (this.masterSelected) {
        this.selectedCoacheesIds.push(item.id);
      }
    });
    this.handleCalendarIconStatus();
    this.updateCoachlogAttendees();
  }

  handleCalendarIconStatus(): void {
    if (this.selectedCoacheesIds.length > 0) {
      this.calendarIconStatus = 'enabled';
    } else {
      this.calendarIconStatus = 'disabled';
    }
  }

  updateCoachlogAttendees() {
    this.attendeeUserProfiles = [];
    this.selectedCoacheesIds.forEach((coacheeId) => {
      let foundUser = false;
      const getUserProfile: NewCoachingSessionUserDto = {
        id: coacheeId,
        profile: {
          id: 0,
          first_name: '',
          last_name: '',
          title: '',
          color: '',
          why: '',
          bio: '',
          website: '',
          facebook: '',
          twitter: '',
          instagram: '',
          googleplus: '',
          youtube: '',
          photo: '',
          photoUrl: '',
          is_influencer: 0,
          created_at: 0,
        },
      };
      if (this.tableData?.length) {
        this.tableData.forEach((eachCoachee) => {
          if (coacheeId === eachCoachee.id) {
            foundUser = true;
            getUserProfile.profile = eachCoachee.profile;
          }
        });
      }

      if (foundUser) {
        if (this.attendeeUserProfiles) {
          if (this.attendeeUserProfiles.indexOf(getUserProfile) === -1) {
            this.attendeeUserProfiles.push(getUserProfile);
          }
        }
      }
    });
  }

  coacheesUpdated() {
    this.getCoachees();
  }
}
